import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PLAYLIST_DISPLAY_HUB_STORY,
  PlaylistDisplayHubStoryResponseType,
  PlaylistDisplayHubStoriesResponseType,
  PlaylistDisplayHubStoryStateType,
  PlaylistDisplayHubStoryType,
} from './types';
import { updatePlaylistDisplayHubStories } from './utils';

const playlistDisplayHubStoryInitialState: PlaylistDisplayHubStoryStateType = {
  playlistDisplayHubStories: null,
  selectedPlaylistDisplayHubStory: null,
  loading: true,
  error: null,
};

export const playlistDisplayHubStorySlice = createSlice({
  name: PLAYLIST_DISPLAY_HUB_STORY,
  initialState: playlistDisplayHubStoryInitialState,
  reducers: {
    // CREATE
    requestCreatePlaylistDisplayHubStory: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.loading = true;
    },
    receiveCreatePlaylistDisplayHubStory: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.loading = false;
    },
    failedCreatePlaylistDisplayHubStory: (
      playlistDisplayHubStory,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHubStory.loading = false;
      playlistDisplayHubStory.error = error;
    },
    // DELETE
    requestDeletePlaylistDisplayHubStory: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.loading = true;
    },
    receiveDeletePlaylistDisplayHubStory: (
      playlistDisplayHubStory,
      { payload: playlistDisplayHubStoryId }: PayloadAction<string>
    ) => {
      if (
        playlistDisplayHubStoryId === playlistDisplayHubStory.selectedPlaylistDisplayHubStory?.id
      ) {
        playlistDisplayHubStory.selectedPlaylistDisplayHubStory = null;
      }
      playlistDisplayHubStory.loading = false;
    },
    failedDeletePlaylistDisplayHubStory: (
      playlistDisplayHubStory,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHubStory.loading = false;
      playlistDisplayHubStory.error = error;
    },

    // DUPLICATE
    requestDuplicatePlaylistDisplayHubStory: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.loading = true;
    },
    receiveDuplicatePlaylistDisplayHubStory: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.loading = false;
    },
    failedDuplicatePlaylistDisplayHubStory: (
      playlistDisplayHubStory,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHubStory.loading = false;
      playlistDisplayHubStory.error = error;
    },

    // UPDATE
    requestUpdatePlaylistDisplayHubStory: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.loading = true;
    },
    receiveUpdatePlaylistDisplayHubStory: (
      playlistDisplayHubStory,
      { payload }: PayloadAction<PlaylistDisplayHubStoryType>
    ) => {
      playlistDisplayHubStory.loading = false;

      if (!playlistDisplayHubStory.playlistDisplayHubStories) return;
      //Insert updated playlistDisplayHubStory into playlistDisplayHubStories array
      playlistDisplayHubStory.playlistDisplayHubStories =
        playlistDisplayHubStory.playlistDisplayHubStories?.map((story) =>
          story.id === payload.id ? payload : story
        );
      //Insert updated playlistDisplayHubStory into selectedPlaylistDisplayHubStory
      if (playlistDisplayHubStory?.selectedPlaylistDisplayHubStory?.id === payload.id) {
        playlistDisplayHubStory.selectedPlaylistDisplayHubStory = payload;
      }
    },
    failedUpdatePlaylistDisplayHubStory: (
      playlistDisplayHubStory,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHubStory.loading = false;
      playlistDisplayHubStory.error = error;
    },
    // GET ALL
    requestGetPlaylistDisplayHubStories: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.loading = true;
    },
    receiveGetPlaylistDisplayHubStories: (
      playlistDisplayHubStory,
      { payload }: PayloadAction<PlaylistDisplayHubStoriesResponseType | null>
    ) => {
      playlistDisplayHubStory.loading = false;
      playlistDisplayHubStory.playlistDisplayHubStories = payload;
    },
    failedGetPlaylistDisplayHubStories: (
      playlistDisplayHubStory,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHubStory.loading = false;
      playlistDisplayHubStory.error = error;
    },
    // SET SELECTED
    requestSetSelectedPlaylistDisplayHubStory: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.error = null;
    },
    receiveSetSelectedPlaylistDisplayHubStory: (
      playlistDisplayHubStory,
      { payload }: PayloadAction<PlaylistDisplayHubStoryResponseType | null>
    ) => {
      playlistDisplayHubStory.selectedPlaylistDisplayHubStory = payload;
    },
    failedSetSelectedPlaylistDisplayHubStory: (
      playlistDisplayHubStory,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHubStory.error = error;
    },
    // REORDER
    requestReorderPlaylistDisplayHubStories: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.error = null;
    },
    receiveReorderPlaylistDisplayHubStories: (
      playlistDisplayHubStory,
      { payload: playlistDisplayHubStoriesIds }: PayloadAction<string[]>
    ) => {
      if (!playlistDisplayHubStory.playlistDisplayHubStories) return;
      playlistDisplayHubStory.playlistDisplayHubStories.sort((a, b) => {
        const indexA = playlistDisplayHubStoriesIds.indexOf(a.id);
        const indexB = playlistDisplayHubStoriesIds.indexOf(b.id);
        if (indexA < indexB) {
          return -1;
        }
        if (indexA > indexB) {
          return 1;
        }
        return 0;
      });
    },
    failedReorderPlaylistDisplayHubStories: (
      playlistDisplayHubStory,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHubStory.error = error;
    },
    // UPDATE MULTIPLE
    requestUpdatePlaylistDisplayHubStories: (playlistDisplayHubStory) => {
      playlistDisplayHubStory.loading = true;
    },
    receiveUpdatePlaylistDisplayHubStories: (
      playlistDisplayHubStory,
      { payload }: PayloadAction<PlaylistDisplayHubStoriesResponseType>
    ) => {
      playlistDisplayHubStory.loading = false;
      playlistDisplayHubStory.playlistDisplayHubStories = updatePlaylistDisplayHubStories(
        payload,
        playlistDisplayHubStory.playlistDisplayHubStories
      );

      if (playlistDisplayHubStory.selectedPlaylistDisplayHubStory) {
        const selectedPlaylistDisplayHubStory = payload.find(
          (story) => story.id === playlistDisplayHubStory.selectedPlaylistDisplayHubStory?.id
        );
        if (selectedPlaylistDisplayHubStory) {
          playlistDisplayHubStory.selectedPlaylistDisplayHubStory = selectedPlaylistDisplayHubStory;
        }
      }
    },
    failedUpdatePlaylistDisplayHubStories: (
      playlistDisplayHubStory,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHubStory.loading = false;
      playlistDisplayHubStory.error = error;
    },
  },
});

const actions = playlistDisplayHubStorySlice.actions;
const playlistDisplayHubStoryReducer = playlistDisplayHubStorySlice.reducer;

export { actions, playlistDisplayHubStoryReducer };
